// @ts-strict-ignore
import React, { useContext } from 'react';
import { useReactFlow } from 'react-flow-renderer';
import type { Action } from '~/dashboard/views/workflows/store/engine/types';
import {
  selectCanvasRect,
  selectGraphBounds,
  selectSimpleIslandMode,
  selectPreviewAction,
} from '~/dashboard/views/workflows/store/redux/selectors';
import { setFocusedNodeId, setHoveredNodeId } from '~/dashboard/views/workflows/store/redux/slice';
import { exitLinkedChildActionThunk } from '~/dashboard/views/workflows/store/redux/thunks';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { EXPAND_ANIMATION_TIME } from './constants';
import { getViewportAtEndOfCanvasX } from './helpers';
import { NodeContainer, StyledBaseSimpleIslandContainer } from './styles';
import type { BaseOnIslandNodeProps } from '../types';
import { selectEvaluationOutcome, selectThresholdOutputResults } from 'flow/store/redux/selectors/workflowFeatures';
import { UserContext } from '~/dashboard/context';
import { ActionType } from 'flow/store/engine/constants';
import { isPeekingIslandMode } from 'flow/service/graph';

interface BaseSimpleIslandProps {
  actions: Action[];
  Node: (props: BaseOnIslandNodeProps) => JSX.Element;
}

export default function BaseSimpleIsland({ actions, Node }: BaseSimpleIslandProps) {
  const { featureFlags } = useContext(UserContext);
  const dispatch = useAppDispatch();
  const islandMode = useAppSelector(selectSimpleIslandMode);
  const graphBounds = useAppSelector(selectGraphBounds);
  const canvasRect = useAppSelector(selectCanvasRect);
  const previewActionBeingBuilt = useAppSelector(selectPreviewAction);
  const evaluationOutcome = useAppSelector(selectEvaluationOutcome);
  const ruleExplainabilityData = useAppSelector(selectThresholdOutputResults);

  const { setViewport } = useReactFlow();

  const handleNodeClick = React.useCallback((e: React.MouseEvent, action: Action) => {
    const isActionTypeOutcome = action.type === ActionType.OUTCOME;
    const isOutcomeSet = isActionTypeOutcome && evaluationOutcome === action.outcome?.name;
    if (!isOutcomeSet && featureFlags?.ruleExplainabilityPhase >= 4 && ruleExplainabilityData) {
      return;
    }

    const { publicId } = action;
    if (previewActionBeingBuilt && publicId !== previewActionBeingBuilt.publicId) {
      dispatch(exitLinkedChildActionThunk(previewActionBeingBuilt.publicId));
    }
    dispatch(setFocusedNodeId(action.publicId));
    if (isPeekingIslandMode(islandMode)) {
      const viewportAtEndOfCanvasX = getViewportAtEndOfCanvasX(graphBounds, canvasRect);
      setViewport(viewportAtEndOfCanvasX, { duration: EXPAND_ANIMATION_TIME });
    }
  }, [canvasRect, dispatch, featureFlags, graphBounds, ruleExplainabilityData,
    islandMode, previewActionBeingBuilt, setViewport, evaluationOutcome]);

  const handleMouseEnter = React.useCallback((e: React.MouseEvent, action: Action) => {
    dispatch(setHoveredNodeId(action.publicId));
  }, [dispatch]);

  const handleMouseLeave = React.useCallback(() => {
    dispatch(setHoveredNodeId(null));
  }, [dispatch]);


  return (
    <StyledBaseSimpleIslandContainer
      islandMode={islandMode}
      data-dd-privacy="allow"
      data-dd-action-name={`outcome-island-${islandMode}`}
    >

      <NodeContainer>
        {
          actions.map(action => (
            <Node
              key={action.publicId}
              action={action}
              onClick={handleNodeClick}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              islandMode={islandMode}
            />
          ))
        }

      </NodeContainer>

    </StyledBaseSimpleIslandContainer>
  );
}
